import {
  BUSINESS_KINDS,
  BUSINESS_KIND_LABELS,
  BUSINESS_KIND_LIST_LABELS,
  type BusinessKind,
  LEAD_SEGMENTS,
  LEAD_SEGMENT_LIST_LABELS,
  type LeadSegment,
  CONSENT_BASIS_HINTS,
  CONSENT_BASIS_LABELS,
  IMPORT_CONSENT_BASES,
  NEXT_ACTION_LABELS,
  type ImportJobDTO,
  type NextAction,
  type OpportunityStage,
  type OwnershipType,
  LEAD_COLUMNS,
  LEAD_COLUMN_LABELS,
  type LeadBulkAction,
  type LeadBulkUpdateResult,
  type LeadColumn,
  type LeadFilter,
  type LeadListResponse,
  type LeadRowDTO,
  type LeadSelection,
  type LeadSortField,
  type SavedViewDTO,
} from '@hearth/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowRightLeft,
  ChefHat,
  Columns3,
  Download,
  Filter,
  Globe,
  Handshake,
  type LucideIcon,
  Mail,
  Plus,
  RefreshCw,
  Search,
  ShieldCheck,
  Store,
  Target,
  Trash2,
  Upload,
  UserCheck,
  Warehouse,
  X,
} from 'lucide-react';
import { type ReactNode, useCallback, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { Page } from '../../components/layout/Page';
import { PageHeader } from '../../components/layout/PageHeader';
import { MoneyText, NextActionChip, OwnerAvatar, RelationshipBadge, RelativeTime, ScorePill, StageBadge, TagChip, TierBadge } from '../../components/crm';
import {
  Badge,
  Button,
  Card,
  Checkbox,
  DropdownMenu,
  EmptyState,
  IconButton,
  Input,
  MultiSelect,
  type MultiSelectOption,
  Pagination,
  SidePanel,
  Spinner,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
  useConfirm,
  useToast,
} from '../../components/ui';
import { useAuth } from '../../hooks/useAuth';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { useHotkeys } from '../../hooks/useHotkeys';
import { useLookups } from '../../hooks/useMeta';
import { api, apiUrl, errorMessage } from '../../lib/api';
import { formatDate, formatNumber, pluralize } from '../../lib/format';
import {
  activeFilterCount,
  allOnPageSelected,
  applyPatch,
  columnsFromParams,
  filterChips,
  filterForKind,
  kindCount,
  kindNoun,
  listOf,
  loadColumnPreference,
  paramsFromState,
  requestFilter,
  saveColumnPreference,
  stateFromParams,
  tableDefaults,
  toggleId,
} from './model';

const SORTABLE: Partial<Record<LeadColumn, LeadSortField>> = {
  business: 'name',
  city: 'city',
  score: 'score',
  stage: 'stage',
  relationship: 'relationship',
  lastContactedAt: 'lastContactedAt',
  nextFollowUpAt: 'nextFollowUpAt',
  estimatedValue: 'estimatedValue',
  createdAt: 'createdAt',
};

type ConsentAction = Extract<LeadBulkAction, { type: 'set_consent' }>;
const BULK_CONSENT_BASES = IMPORT_CONSENT_BASES.filter((basis): basis is ConsentAction['consentBasis'] => basis !== 'none');

/** Toast for 'Record email consent': contacts changed, contacts left alone, and leads skipped with why. */
function consentSummary(result: LeadBulkUpdateResult): string {
  const { updated, created, skipped: kept } = result.consent ?? { updated: 0, created: 0, skipped: 0 };
  const parts = [
    updated + created > 0
      ? `Consent recorded for ${pluralize(updated + created, 'contact')}${created > 0 ? ` (${created} added for a business email)` : ''}`
      : 'No new consent recorded',
    kept > 0 ? `${pluralize(kept, 'contact')} already had consent recorded or withdrawn, left as is` : null,
    result.skipped.length > 0 ? `${pluralize(result.skipped.length, 'lead')} skipped: ${[...new Set(result.skipped.map((s) => s.reason.toLowerCase()))].join('; ')}` : null,
  ];
  return `${parts.filter(Boolean).join('. ')}.`;
}

/** Toast for 'Find emails on websites': the lookups run in the background, so say what was queued. */
function websiteLookupSummary(result: LeadBulkUpdateResult): string {
  const { queued, sites } = result.websiteLookups ?? { queued: 0, sites: 0 };
  const skipped = result.skipped.length > 0 ? ` ${pluralize(result.skipped.length, 'lead')} skipped.` : '';
  if (queued === 0) return `No websites to read.${skipped}`;
  return `Reading ${pluralize(sites, 'website')} for ${pluralize(queued, 'lead')} — addresses appear on the leads as they are found.${skipped}`;
}

/** Page copy for each list; the titles come from BUSINESS_KIND_LIST_LABELS / LEAD_SEGMENT_LIST_LABELS. */
const LIST_COPY: Record<BusinessKind, { description: string; emptyDescription: string; icon: LucideIcon }> = {
  pizzeria: {
    description: 'Every pizzeria and restaurant you are working.',
    emptyDescription: 'Discover pizzerias near you, import a CSV of existing leads, or add one manually.',
    icon: Store,
  },
  dealer: {
    description: 'Equipment dealers that resell our ovens.',
    emptyDescription: 'Add a dealer you work with, or import a list of them.',
    icon: Warehouse,
  },
  rep: {
    description: 'Independent rep agencies that represent our lines.',
    emptyDescription: 'Add a rep agency that represents you, or import a list of them.',
    icon: Handshake,
  },
};

/** Pizzerias moved by hand out of Leads: engaged prospects, existing clients and test kitchens. */
const SEGMENT_COPY: Record<Exclude<LeadSegment, 'lead'>, { description: string; emptyDescription: string; icon: LucideIcon }> = {
  prospect: {
    description: 'Engaged pizzerias you are actively working toward a sale.',
    emptyDescription: 'Move a lead here from its profile or the Leads list (Move to… → Prospects) once it is engaged.',
    icon: Target,
  },
  client: {
    description: 'Pizzerias that are already clients.',
    emptyDescription: 'Move a lead or prospect here from its profile or list (Move to… → Clients) once they are a client.',
    icon: UserCheck,
  },
  test_kitchen: {
    description: 'Pizzerias set up as test kitchens for our equipment.',
    emptyDescription: 'Move a lead, prospect or client here from its profile or list (Move to… → Test Kitchens).',
    icon: ChefHat,
  },
};

/**
 * The lead table: the screen a pizza-equipment salesperson lives in. Saved views, filters in the URL,
 * sortable columns, column customization, bulk actions and a hand-off to bulk email. Leads, Prospects, Clients,
 * Test Kitchens, Dealers and Reps are the same table scoped to one kind of business (and, for pizzerias, one segment).
 */
export function LeadsPage({ kind = 'pizzeria', segment = 'lead' }: { kind?: BusinessKind; segment?: LeadSegment }) {
  // Keyed so going from Leads to Dealers starts fresh: the routes render the same component, and a selection or
  // typed search must not carry over to the other list.
  return <LeadList key={`${kind}:${segment}`} kind={kind} segment={segment} />;
}

function LeadList({ kind, segment }: { kind: BusinessKind; segment: LeadSegment }) {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();
  const { meta, lookups } = useLookups();
  const { can } = useAuth();
  const isPizzeria = kind === 'pizzeria';
  const listLabel = listOf({ kind, segment }).label;
  const copy = kind === 'pizzeria' && segment !== 'lead' ? SEGMENT_COPY[segment] : LIST_COPY[kind];
  const noun = kindNoun(kind);
  const newLeadPath = `/leads/new?kind=${kind}${kind === 'pizzeria' && segment !== 'lead' ? `&segment=${segment}` : ''}`;
  const defaults = useMemo(() => tableDefaults(kind), [kind]);

  const state = useMemo(() => stateFromParams(params, defaults), [params, defaults]);
  // What the page shows and requests: pizzeria-only filters of a saved view do not apply on Dealers/Reps.
  const shownFilter = filterForKind(state.filter, kind);
  // Precedence: the URL (saved view / shared link) → this user's saved preference → built-in default.
  const columns = useMemo<LeadColumn[]>(
    () => columnsFromParams(params) ?? loadColumnPreference(kind) ?? defaults.columns,
    [params, kind, defaults],
  );
  const [search, setSearch] = useState(state.filter.q ?? '');
  const debouncedSearch = useDebouncedValue(search, 250);
  const [selected, setSelected] = useState<string[]>([]);
  const [selectAllMatching, setSelectAllMatching] = useState(false);
  const [excluded, setExcluded] = useState<string[]>([]);
  const [showFilters, setShowFilters] = useState(false);
  const [showColumns, setShowColumns] = useState(false);
  const [busy, setBusy] = useState(false);

  const query = { ...requestFilter(state.filter, kind, segment), sort: state.sort, dir: state.dir, page: state.page, pageSize: state.pageSize };
  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['leads', 'list', query],
    queryFn: ({ signal }) => api.get<LeadListResponse>(apiUrl('/api/leads', query), { signal }),
    placeholderData: (previous) => previous,
  });
  // The import's file name for its filter chip (shared cache with the import screen).
  const importJobId = state.filter.importJobId;
  const { data: importJob } = useQuery({
    queryKey: ['imports', importJobId],
    queryFn: ({ signal }) => api.get<ImportJobDTO>(`/api/imports/${importJobId}`, { signal }),
    enabled: Boolean(importJobId),
    retry: false,
  });
  // Same for a folder import: its file count (shared cache with the batch page).
  const importBatchId = state.filter.importBatchId;
  const { data: importBatch } = useQuery({
    queryKey: ['imports', 'list', { batchId: importBatchId }],
    queryFn: ({ signal }) => api.get<{ items: ImportJobDTO[] }>(apiUrl('/api/imports', { batchId: importBatchId }), { signal }),
    enabled: Boolean(importBatchId),
    retry: false,
  });

  const { data: views } = useQuery({
    queryKey: ['views'],
    queryFn: ({ signal }) => api.get<{ items: SavedViewDTO[] }>('/api/views', { signal }),
  });

  const update = useCallback(
    (patch: Partial<typeof state>, options: { resetSelection?: boolean } = {}) => {
      const next = { ...state, ...patch };
      if (patch.filter) next.page = 1;
      if (!patch.page) next.page = patch.filter ? 1 : next.page;
      setParams(paramsFromState(next, columns, defaults), { replace: true });
      if (options.resetSelection !== false) {
        setSelected([]);
        setSelectAllMatching(false);
        setExcluded([]);
      }
    },
    [state, columns, defaults, setParams],
  );

  const applyFilterPatch = useCallback(
    (patch: Partial<LeadFilter>) => update({ filter: applyPatch(state.filter, patch), viewId: null }),
    [state.filter, update],
  );

  // Search input follows the URL when a saved view or a chip changes it.
  const urlQ = state.filter.q ?? '';
  if (urlQ !== search && !debouncedSearch) setSearch(urlQ);
  const searchRef = useMemo(() => ({ current: null as HTMLInputElement | null }), []);
  useHotkeys({
    '/': () => searchRef.current?.focus(),
    n: () => navigate(newLeadPath),
  });

  const pageIds = data?.items.map((row) => row.id) ?? [];
  const selection: LeadSelection | null = selectAllMatching
    ? { mode: 'filter', filter: requestFilter(state.filter, kind, segment), excludeIds: excluded }
    : selected.length > 0
      ? { mode: 'ids', ids: selected }
      : null;
  const selectionTotal = selectAllMatching ? Math.max(0, (data?.total ?? 0) - excluded.length) : selected.length;
  const filterCount = activeFilterCount(shownFilter);

  const bulk = useMutation({
    mutationFn: (action: Record<string, unknown>) => api.post<LeadBulkUpdateResult>('/api/leads/bulk-update', { selection, action }),
    onSuccess: (result, action) => {
      const skipped = result.skipped.length > 0 ? `, skipped ${result.skipped.length}` : '';
      toast.success(
        action.type === 'set_kind'
          ? `Moved ${formatNumber(result.updated)} to ${BUSINESS_KIND_LIST_LABELS[action.kind as BusinessKind]}${skipped}`
          : action.type === 'set_segment'
            ? `Moved ${formatNumber(result.updated)} to ${LEAD_SEGMENT_LIST_LABELS[action.segment as LeadSegment]}${skipped}`
          : result.websiteLookups
            ? websiteLookupSummary(result)
            : result.consent
              ? consentSummary(result)
              : `Updated ${kindCount(kind, result.updated)}${skipped}`,
      );
      setSelected([]);
      setSelectAllMatching(false);
      setExcluded([]);
      void queryClient.invalidateQueries({ queryKey: ['leads'] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const runBulk = async (action: Record<string, unknown>, confirmation?: { title: string; message: ReactNode; requireText?: string; confirmLabel?: string }) => {
    if (confirmation) {
      const ok = await confirm({ confirmLabel: 'Apply', ...confirmation, danger: action.type === 'delete' });
      if (!ok) return;
    }
    setBusy(true);
    try {
      await bulk.mutateAsync(action);
    } finally {
      setBusy(false);
    }
  };

  const exportUrl = apiUrl('/api/leads/export', { ...requestFilter(state.filter, kind, segment), columns: columns.join(',') });

  return (
    <Page width="full">
      <PageHeader
        title={listLabel}
        description={
          data
            ? isPizzeria
              ? `${pluralize(data.total, 'pizzeria', 'pizzerias')} and restaurants matching the current filters`
              : `${kindCount(kind, data.total)} matching the current filters`
            : copy.description
        }
        actions={
          <>
            {can('lead:export') && (
              <Button icon={Download} onClick={() => window.open(exportUrl, '_blank')}>
                Export
              </Button>
            )}
            {can('lead:import') && (
              <Button icon={Upload} onClick={() => navigate(isPizzeria ? '/imports' : `/imports?kind=${kind}`)}>
                Import
              </Button>
            )}
            <Button variant="primary" icon={Plus} onClick={() => navigate(newLeadPath)}>
              New {noun}
            </Button>
          </>
        }
      />

      <Card className="overflow-hidden">
        <div className="flex flex-wrap items-center gap-2 border-b border-slate-200 p-3">
          <div className="relative min-w-[220px] flex-1">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-slate-400" />
            <Input
              ref={(element) => {
                searchRef.current = element;
              }}
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                update({ filter: applyPatch(state.filter, { q: event.target.value || undefined }), viewId: null });
              }}
              placeholder="Search name, contact, phone, email, note…  (press /)"
              className="pl-8"
              aria-label={`Search ${listLabel.toLowerCase()}`}
            />
          </div>
          <MultiSelect
            label="Stage"
            options={(meta?.stages ?? []).map((stage) => ({ value: stage.key, label: stage.label, color: stage.color }))}
            value={state.filter.stageKeys ?? []}
            onChange={(values) => applyFilterPatch({ stageKeys: values.length > 0 ? values : undefined })}
          />
          {isPizzeria && (
            <MultiSelect
              label="Tier"
              options={(meta?.tiers ?? []).map((tier) => ({ value: tier.key, label: tier.label, color: tier.color }))}
              value={state.filter.tierKeys ?? []}
              onChange={(values) => applyFilterPatch({ tierKeys: values.length > 0 ? values : undefined })}
            />
          )}
          <MultiSelect
            label="Owner"
            searchable
            options={[{ value: 'me', label: 'Me' }, { value: 'unassigned', label: 'Unassigned' }, ...(meta?.users ?? []).map((u) => ({ value: u.id, label: u.name }))]}
            value={state.filter.owner ?? []}
            onChange={(values) => applyFilterPatch({ owner: values.length > 0 ? values : undefined })}
          />
          <Button icon={Filter} onClick={() => setShowFilters(true)}>
            More filters{filterCount > 0 ? ` (${filterCount})` : ''}
          </Button>
          <IconButton
            icon={Columns3}
            label="Choose columns"
            onClick={() => setShowColumns(true)}
          />
          <IconButton icon={RefreshCw} label="Refresh" loading={isFetching} onClick={() => void queryClient.invalidateQueries({ queryKey: ['leads', 'list'] })} />
        </div>

        {(views?.items?.length ?? 0) > 0 && (
          <div className="flex flex-wrap items-center gap-1.5 border-b border-slate-200 px-3 py-2">
            <button
              type="button"
              onClick={() => update({ filter: {}, viewId: null, sort: defaults.sort, dir: defaults.dir })}
              className={`rounded-full px-2.5 py-1 text-xs font-medium ${state.viewId === null ? 'bg-ember-100 text-ember-800' : 'text-slate-600 hover:bg-slate-100'}`}
            >
              All {listLabel.toLowerCase()}
            </button>
            {(views?.items ?? []).map((view) => (
              <button
                key={view.id}
                type="button"
                onClick={() => {
                  const params = new URLSearchParams(paramsFromState({ ...state, ...stateFromParams(new URLSearchParams(), defaults) }, columns, defaults));
                  for (const [key, value] of Object.entries(view.filters as Record<string, unknown>)) {
                    if (value === undefined || value === null || value === '') continue;
                    params.set(key, Array.isArray(value) ? value.join(',') : String(value));
                  }
                  if (view.sortBy) params.set('sort', String(view.sortBy));
                  if (view.sortDir) params.set('dir', String(view.sortDir));
                  params.set('view', view.id);
                  setParams(params, { replace: true });
                  setSelected([]);
                  setSelectAllMatching(false);
                }}
                className={`rounded-full px-2.5 py-1 text-xs font-medium ${state.viewId === view.id ? 'bg-ember-100 text-ember-800' : 'text-slate-600 hover:bg-slate-100'}`}
                title={view.isShared ? 'Shared view' : 'My view'}
              >
                {view.name}
                {view.isShared && <span className="ml-1 text-[10px] uppercase tracking-wide text-slate-400">shared</span>}
              </button>
            ))}
            <Button
              size="sm"
              variant="ghost"
              onClick={() =>
                void (async () => {
                  const name = await prompt('Name this view');
                  if (!name) return;
                  try {
                    await api.post('/api/views', { name, filters: state.filter, sortBy: state.sort, sortDir: state.dir, columns, isShared: false });
                    toast.success('View saved');
                    await queryClient.invalidateQueries({ queryKey: ['views'] });
                  } catch (error) {
                    toast.error(errorMessage(error));
                  }
                })()
              }
            >
              Save this view
            </Button>
          </div>
        )}

        {filterCount > 0 && (
          <div className="flex flex-wrap items-center gap-1.5 border-b border-slate-200 px-3 py-2">
            {filterChips(shownFilter, {
              stage: (key) => lookups.stageByKey(key)?.label ?? key,
              relationship: (key) => lookups.relationshipByKey(key)?.label ?? key,
              tier: (key) => lookups.tierByKey(key)?.label ?? key,
              user: (id) => lookups.userById(id)?.name ?? 'Unknown',
              tag: (id) => lookups.tagById(id)?.name ?? 'Tag',
              source: (id) => lookups.sourceById(id)?.name ?? 'Source',
              importJob: () => importJob?.filename ?? 'a file',
              importBatchFiles: () => importBatch?.items.length,
            }).map((chip) => (
              <span key={chip.key} className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-xs text-slate-700">
                {chip.label}
                <button type="button" aria-label={`Remove ${chip.label}`} onClick={() => applyFilterPatch(chip.clear)} className="text-slate-400 hover:text-slate-700">
                  <X className="size-3" />
                </button>
              </span>
            ))}
            <Button size="sm" variant="ghost" onClick={() => update({ filter: {}, viewId: null })}>
              Clear all
            </Button>
          </div>
        )}

        {selectionTotal > 0 && (
          <div className="flex flex-wrap items-center gap-2 border-b border-ember-200 bg-ember-50 px-3 py-2 text-[13px]">
            <span className="font-medium text-ember-900">{kindCount(kind, selectionTotal)} selected</span>
            {!selectAllMatching && selected.length === pageIds.length && (data?.total ?? 0) > pageIds.length && (
              <Button size="sm" variant="ghost" onClick={() => setSelectAllMatching(true)}>
                Select all {formatNumber(data?.total ?? 0)} matching filters
              </Button>
            )}
            <div className="ml-auto flex flex-wrap items-center gap-1.5">
              <MultiSelect
                label="Assign to"
                options={[{ value: 'unassigned', label: 'Nobody' }, ...(meta?.users ?? []).map((u) => ({ value: u.id, label: u.name }))]}
                value={[]}
                onChange={(values) => {
                  const chosen = values.at(-1);
                  if (chosen === undefined) return;
                  void runBulk({ type: 'assign', ownerId: chosen === 'unassigned' ? null : chosen });
                }}
              />
              <MultiSelect
                label="Stage"
                options={(meta?.stages ?? []).map((stage) => ({ value: stage.key, label: stage.label, color: stage.color }))}
                value={[]}
                onChange={(values) => {
                  const chosen = values.at(-1);
                  if (chosen) void runBulk({ type: 'stage', stageKey: chosen });
                }}
              />
              <MultiSelect
                label="Tags"
                options={(meta?.tags ?? []).map((tag) => ({ value: tag.id, label: tag.name, color: tag.color }))}
                value={[]}
                onChange={(values) => {
                  const chosen = values.at(-1);
                  if (chosen) void runBulk({ type: 'add_tags', tagIds: [chosen] });
                }}
              />
              {can('lead:write') && (
                <DropdownMenu
                  aria-label="Move to another list"
                  trigger={(props) => (
                    <Button {...props} size="sm" icon={ArrowRightLeft}>
                      Move to…
                    </Button>
                  )}
                  items={[
                    // Pizzerias move between Leads, Prospects, Clients and Test Kitchens; any business can change kind.
                    ...(isPizzeria ? LEAD_SEGMENTS.filter((target) => target !== segment) : []).map((target) => ({
                      key: `segment:${target}`,
                      label: LEAD_SEGMENT_LIST_LABELS[target],
                      onSelect: () =>
                        void runBulk({ type: 'set_segment', segment: target } satisfies LeadBulkAction, {
                          title: `Move ${kindCount(kind, selectionTotal)} to ${LEAD_SEGMENT_LIST_LABELS[target]}?`,
                          confirmLabel: 'Move',
                          message: `They leave ${listLabel} and appear under ${LEAD_SEGMENT_LIST_LABELS[target]}. Their contacts, history, deals and tasks stay with them.`,
                        }),
                    })),
                    ...BUSINESS_KINDS.filter((target) => target !== kind).map((target) => ({
                      key: `kind:${target}`,
                      label: BUSINESS_KIND_LIST_LABELS[target],
                      onSelect: () =>
                        void runBulk({ type: 'set_kind', kind: target } satisfies LeadBulkAction, {
                          title: `Move ${kindCount(kind, selectionTotal)} to ${BUSINESS_KIND_LIST_LABELS[target]}?`,
                          confirmLabel: 'Move',
                          message: `They leave ${listLabel} and appear under ${BUSINESS_KIND_LIST_LABELS[target]}. Their contacts, history, deals and tasks stay with them.`,
                        }),
                    })),
                  ]}
                />
              )}
              <DropdownMenu
                aria-label="Record email consent"
                className="w-[34rem] max-w-[calc(100vw-1rem)]"
                trigger={(props) => (
                  <Button {...props} size="sm" icon={ShieldCheck}>
                    Record email consent
                  </Button>
                )}
                items={[
                  { type: 'label', key: 'question', label: 'Where did their email addresses come from?' },
                  ...BULK_CONSENT_BASES.map((consentBasis) => ({
                    key: consentBasis,
                    label: CONSENT_BASIS_HINTS[consentBasis],
                    onSelect: () =>
                      void runBulk({ type: 'set_consent', consentBasis } satisfies ConsentAction, {
                        title: `Record “${CONSENT_BASIS_LABELS[consentBasis]}” for ${kindCount(kind, selectionTotal)}?`,
                        confirmLabel: 'Record consent',
                        message: (
                          <>
                            <p>
                              {CONSENT_BASIS_HINTS[consentBasis]}. Contacts with an email address and no consent yet get this consent, dated today and noted as
                              recorded by you. Consent already recorded or withdrawn is never changed. A lead with only a business email gets a contact for that address.
                            </p>
                            <p className="mt-2 text-xs text-slate-500">
                              Canada’s anti-spam law (CASL) allows bulk email with implied consent — an address the business published, a customer from the last 2 years
                              or an inquiry from the last 6 months — or with express consent, when they agreed to receive your emails.
                            </p>
                          </>
                        ),
                      }),
                  })),
                ]}
              />
              {can('lead:write') && (
                <Button
                  size="sm"
                  icon={Globe}
                  onClick={() =>
                    void runBulk({ type: 'find_website_email' } satisfies LeadBulkAction, {
                      title: `Find emails on the websites of ${kindCount(kind, selectionTotal)}?`,
                      confirmLabel: 'Find emails',
                      message: (
                        <>
                          <p>
                            Hearth reads each lead’s own website (the home page and up to two contact pages, respecting robots.txt) for the email address it
                            publishes. Leads without a website are skipped. It runs in the background; addresses appear on the leads as they are found.
                          </p>
                          <p className="mt-2 text-xs text-slate-500">
                            An address the business publishes on its own website is recorded with implied consent (a published address) and the page as evidence,
                            unless the site asks for no solicitations.
                          </p>
                        </>
                      ),
                    })
                  }
                >
                  Find emails on websites
                </Button>
              )}
              <Button
                size="sm"
                icon={Mail}
                onClick={() =>
                  navigate('/email/bulk', {
                    state: {
                      selection,
                      count: selectionTotal,
                      description: state.filter.q
                        ? `Filtered ${listLabel.toLowerCase()} matching “${state.filter.q}”`
                        : importJob
                          ? `${listLabel} from import “${importJob.filename}”`
                          : `Selected ${listLabel.toLowerCase()}`,
                    },
                  })
                }
              >
                Send email
              </Button>
              {can('lead:delete') && (
                <Button
                  size="sm"
                  variant="danger"
                  icon={Trash2}
                  onClick={() =>
                    void runBulk(
                      { type: 'delete' },
                      {
                        title: `Delete ${kindCount(kind, selectionTotal)}?`,
                        message: 'They are hidden from the pipeline but can be restored by a manager.',
                        requireText: selectionTotal >= (meta?.settings.bulkConfirmThreshold ?? 25) ? `DELETE ${selectionTotal}` : undefined,
                      },
                    )
                  }
                >
                  Delete
                </Button>
              )}
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setSelected([]);
                  setSelectAllMatching(false);
                  setExcluded([]);
                }}
              >
                Clear selection
              </Button>
            </div>
          </div>
        )}

        {isLoading ? (
          <div className="p-10">
            <Spinner />
          </div>
        ) : (data?.items.length ?? 0) === 0 ? (
          <div className="p-6">
            <EmptyState
              icon={copy.icon}
              title={filterCount > 0 ? `No ${listLabel.toLowerCase()} match these filters` : `No ${listLabel.toLowerCase()} yet`}
              description={filterCount > 0 ? 'Try clearing a filter or widening the search.' : copy.emptyDescription}
              action={
                filterCount > 0 ? (
                  <Button onClick={() => update({ filter: {}, viewId: null })}>Clear filters</Button>
                ) : (
                  <div className="flex gap-2">
                    {isPizzeria && (
                      <Button variant="primary" icon={Search} onClick={() => navigate('/discover')}>
                        Discover pizzerias
                      </Button>
                    )}
                    <Button icon={Upload} onClick={() => navigate(isPizzeria ? '/imports' : `/imports?kind=${kind}`)}>
                      Import CSV
                    </Button>
                    <Button variant={isPizzeria ? 'secondary' : 'primary'} icon={Plus} onClick={() => navigate(newLeadPath)}>
                      Add a {noun}
                    </Button>
                  </div>
                )
              }
            />
          </div>
        ) : (
          <>
            <Table containerClassName="max-h-[calc(100vh-22rem)]">
              <THead>
                <TR>
                  <TH className="w-9">
                    <Checkbox
                      aria-label="Select all on this page"
                      checked={allOnPageSelected(pageIds, selected)}
                      onChange={(event) => {
                        const checked = event.target.checked;
                        setSelectAllMatching(false);
                        setSelected(checked ? [...new Set([...selected, ...pageIds])] : selected.filter((id) => !pageIds.includes(id)));
                      }}
                    />
                  </TH>
                  {columns.map((column) => {
                    const sortField = SORTABLE[column];
                    return (
                      <TH
                        key={column}
                        sort={sortField && state.sort === sortField ? state.dir : null}
                        onSort={
                          sortField
                            ? () =>
                                update({
                                  sort: sortField,
                                  dir: state.sort === sortField && state.dir === 'desc' ? 'asc' : 'desc',
                                })
                            : undefined
                        }
                      >
                        {LEAD_COLUMN_LABELS[column]}
                      </TH>
                    );
                  })}
                </TR>
              </THead>
              <TBody>
                {data!.items.map((lead) => (
                  <LeadTableRow
                    key={lead.id}
                    lead={lead}
                    columns={columns}
                    selected={selected.includes(lead.id) || (selectAllMatching && !excluded.includes(lead.id))}
                    onToggle={() => {
                      if (selectAllMatching) {
                        setExcluded((current) => toggleId(current, lead.id));
                        return;
                      }
                      setSelected((current) => toggleId(current, lead.id));
                    }}
                    onOpen={() => navigate(`/leads/${lead.id}`)}
                  />
                ))}
              </TBody>
            </Table>
            <div className="flex items-center justify-between gap-3 border-t border-slate-200 px-3 py-2">
              <Pagination
                page={state.page}
                pageSize={state.pageSize}
                total={data!.total}
                onPageSizeChange={(size) => update({ pageSize: size })}
                onChange={(page) => update({ page })}
              />
            </div>
          </>
        )}
      </Card>

      <SidePanel open={showFilters} onClose={() => setShowFilters(false)} title="Filters" width="md">
        <AdvancedFilters filter={state.filter} onPatch={applyFilterPatch} kind={kind} />
      </SidePanel>

      <SidePanel open={showColumns} onClose={() => setShowColumns(false)} title="Columns" width="md">
        <p className="mb-3 text-xs text-slate-500">Choose what the table shows. Saved views remember their own columns.</p>
        <div className="space-y-1">
          {LEAD_COLUMNS.map((column) => (
            <label key={column} className="flex items-center gap-2 rounded px-2 py-1.5 text-[13px] hover:bg-slate-50">
              <Checkbox
                checked={columns.includes(column)}
                onChange={() => {
                  const next = columns.includes(column) ? columns.filter((c) => c !== column) : [...columns, column];
                  const ordered = LEAD_COLUMNS.filter((c) => next.includes(c));
                  if (ordered.length === 0) return;
                  saveColumnPreference(kind, ordered);
                  setParams(paramsFromState(state, ordered, defaults), { replace: true });
                }}
              />
              {LEAD_COLUMN_LABELS[column]}
            </label>
          ))}
        </div>
      </SidePanel>

      {busy && (
        <div className="fixed bottom-4 right-4 z-50 flex items-center gap-2 rounded-lg border border-slate-200 bg-surface px-3 py-2 shadow-panel">
          <Spinner className="size-4" />
          <span className="text-[13px] text-slate-600">Working…</span>
        </div>
      )}
    </Page>
  );
}

/** Option lists are typed as MultiSelectOption (value: string) so filter state (string[]) fits directly. */
const OPPORTUNITY_STAGE_OPTIONS: MultiSelectOption[] = [
  { value: 'qualifying', label: 'Qualifying' },
  { value: 'quote_requested', label: 'Quote requested' },
  { value: 'quote_sent', label: 'Quote sent' },
  { value: 'negotiation', label: 'Negotiation' },
];
const NEXT_ACTION_OPTIONS: MultiSelectOption[] = (Object.keys(NEXT_ACTION_LABELS) as NextAction[]).map((value) => ({
  value,
  label: NEXT_ACTION_LABELS[value],
}));
const OWNERSHIP_OPTIONS: MultiSelectOption[] = [
  { value: 'independent', label: 'Independent' },
  { value: 'chain', label: 'Chain' },
  { value: 'franchise', label: 'Franchise' },
  { value: 'group', label: 'Restaurant group' },
];
const MISSING_OPTIONS: MultiSelectOption[] = [
  { value: 'email', label: 'No email' },
  { value: 'phone', label: 'No phone' },
  { value: 'contact', label: 'No contact' },
  { value: 'decision_maker', label: 'No decision maker' },
  { value: 'address', label: 'No address' },
  { value: 'website', label: 'No website' },
];

function LeadTableRow({
  lead,
  columns,
  selected,
  onToggle,
  onOpen,
}: {
  lead: LeadRowDTO;
  columns: LeadColumn[];
  selected: boolean;
  onToggle: () => void;
  onOpen: () => void;
}) {
  const { lookups } = useLookups();
  const contact = lead.primaryContact;
  return (
    <TR
      selected={selected}
      interactive
      onClick={(event) => {
        if ((event.target as HTMLElement).closest('input,button,a')) return;
        onOpen();
      }}
      onKeyDown={(event) => {
        if (event.key === 'Enter') onOpen();
      }}
      tabIndex={0}
    >
      <TD onClick={(event) => event.stopPropagation()}>
        <Checkbox aria-label={`Select ${lead.name}`} checked={selected} onChange={onToggle} />
      </TD>
      {columns.map((column) => (
        <TD key={column} numeric={column === 'score' || column === 'estimatedValue'}>
          {renderCell(column, lead, lookups, contact)}
        </TD>
      ))}
    </TR>
  );
}

function renderCell(
  column: LeadColumn,
  lead: LeadRowDTO,
  lookups: ReturnType<typeof useLookups>['lookups'],
  contact: LeadRowDTO['primaryContact'],
) {
  switch (column) {
    case 'business':
      return (
        <div className="min-w-0">
          <div className="flex items-center gap-1.5">
            <button type="button" className="truncate font-medium text-slate-900 hover:text-ember-700" onClick={(event) => { event.stopPropagation(); window.location.assign(`/leads/${lead.id}`); }}>
              {lead.name}
            </button>
            {lead.doNotContact && (
              <Badge color="red" size="sm">
                DNC
              </Badge>
            )}
            {lead.deletedAt && (
              <Badge color="gray" size="sm">
                Deleted
              </Badge>
            )}
          </div>
          <div className="truncate text-xs text-slate-500">
            {lead.region ? `${lead.city ?? '—'}, ${lead.region}` : (lead.city ?? '—')}
          </div>
        </div>
      );
    case 'kind':
      return <span className="text-slate-600">{BUSINESS_KIND_LABELS[lead.kind]}</span>;
    case 'city':
      return <span className="text-slate-600">{lead.city ?? '—'}</span>;
    // Fit score and tier only exist for pizzerias.
    case 'score':
      return lead.kind === 'pizzeria' ? <ScorePill score={lead.score} tierKey={lead.tierKey} /> : <span className="text-slate-400">—</span>;
    case 'tier':
      return lead.kind === 'pizzeria' ? <TierBadge tierKey={lead.tierKey} /> : <span className="text-slate-400">—</span>;
    case 'stage':
      return <StageBadge stageId={lead.stageId} />;
    case 'relationship':
      return <RelationshipBadge relationshipId={lead.relationshipId} />;
    case 'contact':
      return contact ? (
        <div className="min-w-0">
          <div className="truncate text-slate-800">{contact.name}</div>
          <div className="truncate text-xs text-slate-500">{contact.title ?? contact.email ?? contact.phone ?? ''}</div>
        </div>
      ) : (
        <span className="text-xs text-amber-700">No contact yet</span>
      );
    case 'phone':
      return <span className="text-slate-600">{contact?.phone ?? lead.phone ?? '—'}</span>;
    case 'email':
      return <span className="truncate text-slate-600">{contact?.email ?? lead.email ?? '—'}</span>;
    case 'lastContactedAt':
      return <RelativeTime value={lead.lastContactedAt} />;
    case 'nextFollowUpAt':
      return lead.nextFollowUpAt && new Date(lead.nextFollowUpAt).getTime() < Date.now() ? (
        <span className="text-red-600">
          <RelativeTime value={lead.nextFollowUpAt} granularity="day" />
        </span>
      ) : (
        <RelativeTime value={lead.nextFollowUpAt} granularity="day" />
      );
    case 'nextAction':
      return <NextActionChip action={lead.nextAction} reason={lead.nextActionReason} />;
    case 'estimatedValue':
      return <MoneyText amount={lead.opportunityValue} currency={lead.currency} />;
    case 'owner':
      return <OwnerAvatar userId={lead.ownerId} showName />;
    case 'source':
      return <span className="text-slate-600">{lookups.sourceById(lead.sourceId)?.name ?? '—'}</span>;
    case 'tags':
      return (
        <div className="flex flex-wrap gap-1">
          {lead.tagIds.slice(0, 3).map((id) => (
            <TagChip key={id} tagId={id} />
          ))}
          {lead.tagIds.length > 3 && <span className="text-xs text-slate-500">+{lead.tagIds.length - 3}</span>}
        </div>
      );
    case 'createdAt':
      return <span className="text-slate-600">{formatDate(lead.createdAt)}</span>;
    default:
      return null;
  }
}

function AdvancedFilters({
  filter,
  onPatch,
  kind,
}: {
  filter: LeadFilter;
  onPatch: (patch: Partial<LeadFilter>) => void;
  kind: BusinessKind;
}) {
  const { meta } = useLookups();
  // Score range and ownership describe pizzeria fit; Dealers and Reps do not offer them.
  const isPizzeria = kind === 'pizzeria';
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        {isPizzeria && (
          <>
            <Input
              type="number"
              min={0}
              max={100}
              placeholder="Min score"
              defaultValue={filter.minScore ?? ''}
              onBlur={(event) => onPatch({ minScore: event.target.value === '' ? undefined : Number(event.target.value) })}
            />
            <Input
              type="number"
              min={0}
              max={100}
              placeholder="Max score"
              defaultValue={filter.maxScore ?? ''}
              onBlur={(event) => onPatch({ maxScore: event.target.value === '' ? undefined : Number(event.target.value) })}
            />
          </>
        )}
        <Input placeholder="City" defaultValue={filter.city ?? ''} onBlur={(event) => onPatch({ city: event.target.value || undefined })} />
        <Input placeholder="Region (QC, ON…)" defaultValue={filter.region ?? ''} onBlur={(event) => onPatch({ region: event.target.value || undefined })} />
      </div>
      <MultiSelect
        label="Relationship"
        options={(meta?.relationships ?? []).map((r) => ({ value: r.key, label: r.label, color: r.color }))}
        value={filter.relationshipKeys ?? []}
        onChange={(values) => onPatch({ relationshipKeys: values.length > 0 ? values : undefined })}
      />
      <MultiSelect
        label="Open deal stage"
        options={OPPORTUNITY_STAGE_OPTIONS}
        value={filter.opportunityStages ?? []}
        onChange={(values) => onPatch({ opportunityStages: values.length > 0 ? (values as OpportunityStage[]) : undefined })}
      />
      <MultiSelect
        label="Next action"
        options={NEXT_ACTION_OPTIONS}
        value={filter.nextActions ?? []}
        onChange={(values) => onPatch({ nextActions: values.length > 0 ? (values as NextAction[]) : undefined })}
      />
      <MultiSelect
        label="Tags"
        options={(meta?.tags ?? []).map((tag) => ({ value: tag.id, label: tag.name, color: tag.color }))}
        value={filter.tagIds ?? []}
        onChange={(values) => onPatch({ tagIds: values.length > 0 ? values : undefined })}
      />
      <MultiSelect
        label="Source"
        options={(meta?.sources ?? []).map((source) => ({ value: source.id, label: source.name }))}
        value={filter.sourceIds ?? []}
        onChange={(values) => onPatch({ sourceIds: values.length > 0 ? values : undefined })}
      />
      {isPizzeria && (
        <MultiSelect
          label="Ownership"
          options={OWNERSHIP_OPTIONS}
          value={filter.ownershipTypes ?? []}
          onChange={(values) => onPatch({ ownershipTypes: values.length > 0 ? (values as OwnershipType[]) : undefined })}
        />
      )}
      <MultiSelect
        label="Missing information"
        options={MISSING_OPTIONS}
        value={filter.missing ?? []}
        onChange={(values) => onPatch({ missing: values.length > 0 ? (values as LeadFilter['missing']) : undefined })}
      />
      <div className="grid grid-cols-2 gap-3">
        <Input
          type="number"
          min={1}
          placeholder="No contact in… days"
          defaultValue={filter.lastContactOlderThanDays ?? ''}
          onBlur={(event) => onPatch({ lastContactOlderThanDays: event.target.value === '' ? undefined : Number(event.target.value) })}
        />
        <Input
          type="number"
          min={1}
          placeholder="Added within… days"
          defaultValue={filter.createdWithinDays ?? ''}
          onBlur={(event) => onPatch({ createdWithinDays: event.target.value === '' ? undefined : Number(event.target.value) })}
        />
      </div>
      <div className="space-y-2">
        {(
          [
            ['neverContacted', 'Never contacted'],
            ['hasOpenOpportunity', 'Has an open deal'],
            ['doNotContact', 'Marked do not contact'],
            ['hasPendingDuplicate', 'Possible duplicate'],
          ] as const
        ).map(([key, label]) => (
          <Checkbox
            key={key}
            label={label}
            checked={filter[key] === true}
            onChange={(event) => onPatch({ [key]: event.target.checked ? true : undefined } as Partial<LeadFilter>)}
          />
        ))}
        <Checkbox
          label="Follow-up overdue"
          checked={filter.followUp === 'overdue'}
          onChange={(event) => onPatch({ followUp: event.target.checked ? 'overdue' : undefined })}
        />
        <Checkbox
          label="Follow-up due today"
          checked={filter.followUp === 'today'}
          onChange={(event) => onPatch({ followUp: event.target.checked ? 'today' : undefined })}
        />
      </div>
    </div>
  );
}
